import { useState } from 'react';
import { Box, Typography, CircularProgress, Tooltip, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { UnifiedAlertsIcons } from './icons';
import PageWrapper from 'components/Common/PageWraper';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import html2canvas from 'html2canvas'
import SeverityTiles, { TILES_CONFIG } from './SeverityTiles/SeverityTiles';
import { TIME_RANGE_OPTIONS } from './AdvancedFilterDrawer';
import { useUnifiedAlertsAzure } from './hooks/useUnifiedAlertsAzure';
import AlertsTable from './AlertsTable/AlertsTableAzure';
import AdvancedFilterDrawerAzure from './AdvancedFilterDrawer/AdvancedFilterDrawerAzure';
import ShutterCapture from 'utils/ShutterCapture';
import ImagePreview from 'utils/ImagePreview';
import CaptureExportControls from 'utils/CaptureExportControls';
import { formattedDate } from './SnowAlerts';

const AzureUnifiedAlertsDashboard = () => {
  const theme = useTheme();
  const p = theme.palette;

  const [activeContent, setActiveContent] = useState<'table' | 'chart'>('table');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false); 

  const [screenCapturing, setScreenCapturing] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [openPreview, setOpenPreview] = useState(false)

  const {
    activeTile,
    page,
    pageSize,
    sortingInfo,
    appliedFilters,
    summary,
    alerts,
    totalAlerts,
    isExporting,
    filterOptions,
    setPage,
    setPageSize,
    setSortingInfo,
    handleTileClick,
    handleApplyFilters,
    handleSaveDefaultFilters,
    handleResetDefaultFilters,
    handleExport,
    isLoading,
    summaryLoading,
  } = useUnifiedAlertsAzure();

  const transformSummaryData = (rawSummary: any) => {
    if (!rawSummary) return null;
    if (rawSummary.CRITICAL && rawSummary.MAJOR && rawSummary.MINOR && rawSummary.WARNING && rawSummary.INFORMATION && rawSummary.RESOLVED) {
      return {
        criticalAlerts: rawSummary.CRITICAL,
        majorAlerts: rawSummary.MAJOR,
        minorAlerts: rawSummary.MINOR,
        warningAlerts: rawSummary.WARNING,
        information: rawSummary.INFORMATION,
        closedAlerts: rawSummary.RESOLVED,
      };
    }
    return null;
  };

  const transformedSummary = summary ? transformSummaryData(summary) : null;
  const getTimeRangeLabel = () => {
  const sessionRange = sessionStorage.getItem('unifiedAlertsDateRangeType');
  const effectiveRange = sessionRange || appliedFilters.timeRange;

  if (effectiveRange === 'custom') {
    const start = appliedFilters.startDate || sessionStorage.getItem('unifiedAlertsStartDate');
    const end = appliedFilters.endDate || sessionStorage.getItem('unifiedAlertsEndDate');

    if (start && end) {
      const startDate = new Date(start).toLocaleDateString('en-GB');
      const endDate = new Date(end).toLocaleDateString('en-GB');
      return `${startDate} – ${endDate}`;
    }

    return 'Custom Range';
  }

  const optionLabel = TIME_RANGE_OPTIONS.find(o => o.value === effectiveRange)?.label;

  if (optionLabel) return optionLabel;

  const labelMap: Record<string, string> = {
    '5m':   'Last 5 Minutes',
    '10m':  'Last 10 Minutes',
    '15m':  'Last 15 Minutes',
    '30m':  'Last 30 Minutes',
    '1h':   'Last 1 Hour',
    'today':'Today',
    '24h':  'Last 24 Hours',
    '7d':   'Last 7 Days',
    '30d':  'Last 30 Days',
    '90d':  'Last 90 Days',
  };

  return labelMap[effectiveRange] || '';
};

const timeRange = getTimeRangeLabel();

    const selectedTile = TILES_CONFIG.find(t => t.key === activeTile);
  
    const handleScreenCapture = async (captureTargetId: string = 'ua-capture-area') => {
      try {
        setScreenCapturing(true)
  
        const element = document.getElementById(captureTargetId)
        if (!element) return
  
        const canvas = await html2canvas(element, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          scrollY: -window.scrollY,
          ignoreElements: (el:any) => ['exclude-camera', 'exclude-export'].includes(el.id)
        })
  
        const image = canvas.toDataURL('image/png')
  
        setPreviewImage(image)
        setOpenPreview(true)
      } catch (err) {
        console.error('Screenshot failed', err)
      } finally {
        setScreenCapturing(false)
      }
    }

  return (
    <PageWrapper sx={{ minHeight: 'calc(100vh - 120px)', backgroundColor: p.backgroundLight.sideNavBgHover, position: 'relative' }}>
      <Box
        component="button"
        type="button"
        aria-label="Advanced Filters"
        onClick={() => setIsFilterDrawerOpen(!isFilterDrawerOpen)}
        sx={{
          position: 'absolute',
          top: -80,
          right: isFilterDrawerOpen ? '482px' : 0,
          zIndex: isFilterDrawerOpen ? 1300 : 10,
          width: '58px',
          height: '42px',
          display: 'flex',
          alignItems: 'center',
          p: 0,
          cursor: 'pointer',
          outline: 'none',
          borderRadius: 0,
          backgroundColor: '#FBFDFF',
          boxShadow: '-2px 4px 8px rgba(0, 0, 0, 0.1)',
          border: `1px solid ${p.grey[200]}`,
          transition: 'right 0.3s ease',
          '&:hover': { backgroundColor: p.grey[50] },
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <UnifiedAlertsIcons.ChevronRightIcon width={14} height={14} />
        </Box>
        <Box sx={{ width: '1px', height: '100%', backgroundColor: p.grey[300] }} />
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1 }}>
          <UnifiedAlertsIcons.SettingsSlidersIcon />
        </Box>
      </Box>

      <Box id="ua-main" sx={{ pt: 1, position: 'relative', backgroundColor: p.backgroundLight.sideNavBgHover }}>
        <Box id="ua-capture-area">
                    <Box id="ua-top-tabs" sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                      <Grid container spacing={1} sx={{ flex: 1 }}>
                        <Grid item xs={12}>
                          <SeverityTiles
                            summary={transformedSummary}
                            activeTile={activeContent === 'chart' ? '' : activeTile}
                            onTileClick={(key) => { handleTileClick(key); setActiveContent('table'); }}
                            isLoading={summaryLoading}
                            timePeriod={timeRange}
                            sx={{
                              width: '100% !important',
                              flexWrap: 'wrap',
                              gap: 0.5,
                              '& > div': {
                                flex: '1 1 calc(25% - 4px) !important',
                                maxWidth: 'calc(25% - 4px) !important',
                                boxSizing: 'border-box'
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                      <CaptureExportControls
                        onScreenshot={() => handleScreenCapture('ua-capture-area')}
                        screenCapturing={screenCapturing}
                        onExport={handleExport}
                        isExporting={isExporting}
                        showExport={activeContent === 'table'}
                        isVisible={true}
                        type="azure"
                      />
                    </Box>

                <Box id="ua-tab-content" sx={{ backgroundColor: p.white.main, p: 3, pt: activeContent === 'chart' ? 5 : 3, position: 'relative', minHeight: '300px' }}>

                  <AlertsTable
                    alerts={alerts}
                    total={totalAlerts}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                    onSorting={setSortingInfo}
                    sortingInfo={sortingInfo}
                    isLoading={isLoading}
                    title={selectedTile?.label}
                  />
                </Box>
        </Box>
       
        
      </Box>

      <AdvancedFilterDrawerAzure
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        onApply={handleApplyFilters}
        onSaveDefault={handleSaveDefaultFilters}
        onResetDefault={handleResetDefaultFilters}
        currentFilters={appliedFilters}
        filterOptions={filterOptions}
      />

       <ImagePreview
              open={openPreview}
              image={previewImage}
              fileName={`screenshot_${selectedTile?.label}_${formattedDate}`}
              onClose={() => setOpenPreview(false)}
              title={'Azure Summary Dashboard'}
        />
    </PageWrapper>
  );
};

export default AzureUnifiedAlertsDashboard;